import React, { useContext, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { Box, Drawer, Hidden, List, ListSubheader } from '@mui/material';
import { useShouldEnableMenuEditor } from '@provider-portal/common/experiments/providerOnboarding';
import { useIsMenuEditorRoute } from '@provider-portal/common/routing/hooks';
import { AccountStage } from '@provider-portal/common/services/types/account';
import { renderNavItems } from 'widgets/NavBar/renderNavItems';
import SelectedProvider from 'widgets/NavBar/SelectedProvider';

import {
  selectAccountStageOrNewMerchantVenueOnboarding,
  selectIsSelectedProviderNewMerchantVenueInOnboarding,
  selectSelectedProvider,
} from 'rdx/account/selectors';

import LanguageSelector from 'components/LanguageSelector/LanguageSelector';
import { MenuContext } from 'root/navigation/navStructureUtils';
import type { ConfigMenuCategory, ConfigMenuItem } from 'root/navigation/navTypes';

// almost whole navigation was taking from template library

const menuEditorCategoryId = 'menu-editor';
const menuEditorSummaryItemId = 'org-summary';

const styles = {
  mobileStyle: {
    '& .MuiDrawer-paper': {
      width: '250px',
      backgroundColor: 'colors.white',
      boxShadow: '0px 0px 0px rgba(63, 63, 68, 0.05), 0px 1px 2px rgba(63, 63, 68, 0.15)',
      borderRightWidth: '1px',
      borderRightStyle: 'solid',
      borderRightColor: 'colors.neutral300',
      borderRadius: 'unset',
    },
  },
  desktopStyle: {
    '& .MuiDrawer-paper': {
      top: '74px',
      width: '250px',
      height: 'calc(100vh - 74px)',
      backgroundColor: 'colors.white',
      boxShadow: '0px 0px 0px rgba(63, 63, 68, 0.05), 0px 1px 2px rgba(63, 63, 68, 0.15)',
      borderRightWidth: '1px',
      borderRightStyle: 'solid',
      borderRightColor: 'colors.neutral300',
      borderRadius: 'unset',
    },
  },
  listSubHeader: {
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '16px',
    color: 'colors.neutral900',
  },
  listSubHeaderDisabled: {
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '16px',
    color: 'rgba(0,0,0, 0.26)',
  },
  menuSectionList: {
    padding: '8px 0 24px',
  },
} as const;

interface NavBarProps {
  isMobileView?: boolean;
  onMobileClose: () => void;
}

function isMenuDisabled(
  accountStage: AccountStage,
  menuCategory: ConfigMenuCategory,
  shouldEnableMerchantMenuEditor: boolean,
  isMenuEditorRoute: boolean,
): boolean {
  if (accountStage !== AccountStage.ONBOARDING) {
    return false;
  }

  if (menuCategory.id === menuEditorCategoryId) {
    if (isMenuEditorRoute) {
      return !shouldEnableMerchantMenuEditor;
    }
  }

  return true;
}

function isMenuEditorCategory(category: ConfigMenuCategory): boolean {
  return category.id === menuEditorCategoryId;
}

function isSummaryItem(item: ConfigMenuItem): boolean {
  return item.id === menuEditorSummaryItemId;
}

function pinMenuEditorCategoryAndRemoveSummary(list: ConfigMenuCategory[]): ConfigMenuCategory[] {
  const categoryIndex = list.findIndex(isMenuEditorCategory);
  if (categoryIndex) {
    const [category] = list.splice(categoryIndex, 1);
    const summary = category.items.find(isSummaryItem);
    if (summary) {
      summary.hidden = true;
    }
    list.unshift(category);
  }

  return list;
}

export const NavBar: React.FC<NavBarProps> = ({ isMobileView, onMobileClose }) => {
  const location = useLocation();
  const intl = useIntl();
  const selectedProvider = useSelector(selectSelectedProvider);
  const accountStage = useSelector(selectAccountStageOrNewMerchantVenueOnboarding);
  const menuValue = useContext(MenuContext);
  const shouldEnableMenuEditorForSSU = useShouldEnableMenuEditor();
  const isNewMerchantVenueOnboarding = useSelector(
    selectIsSelectedProviderNewMerchantVenueInOnboarding,
  );
  const shouldEnableMerchantMenuEditor =
    shouldEnableMenuEditorForSSU || isNewMerchantVenueOnboarding;
  const isMenuEditorRoute = useIsMenuEditorRoute();

  const menuCategoryList = useMemo(() => {
    return shouldEnableMerchantMenuEditor
      ? pinMenuEditorCategoryAndRemoveSummary(menuValue.menu)
      : menuValue.menu;
  }, [shouldEnableMerchantMenuEditor, menuValue.menu]);

  const content = useMemo(() => {
    const innerContent = (
      <>
        <SelectedProvider selectedProvider={selectedProvider} />
        <Box mt={3}>
          {menuCategoryList.map((menuCat) => {
            const itemsToRender = menuCat.items;
            const hasMenuItemsDisabled = isMenuDisabled(
              accountStage,
              menuCat,
              shouldEnableMerchantMenuEditor,
              isMenuEditorRoute,
            );
            const subheaderClass = hasMenuItemsDisabled
              ? styles.listSubHeaderDisabled
              : styles.listSubHeader;
            return (
              <List
                sx={styles.menuSectionList}
                key={menuCat.titleIntlId}
                subheader={
                  <ListSubheader disableGutters disableSticky sx={subheaderClass}>
                    {intl.formatMessage({ id: menuCat.titleIntlId })}
                  </ListSubheader>
                }
              >
                {renderNavItems({
                  items: itemsToRender,
                  pathname: location.pathname,
                  disabled: hasMenuItemsDisabled,
                })}
              </List>
            );
          })}
        </Box>
        <Box>
          <LanguageSelector />
        </Box>
      </>
    );

    // perfect scrollbar doesn't change the scrollbar visuals on mobile but does occasionally cause
    //  scrolling issues so let's not use it there
    return isMobileView ? (
      <Box m={3}>{innerContent}</Box>
    ) : (
      <Box m={3}>
        <PerfectScrollbar options={{ suppressScrollX: true }}>{innerContent}</PerfectScrollbar>
      </Box>
    );
  }, [
    selectedProvider,
    menuCategoryList,
    accountStage,
    shouldEnableMerchantMenuEditor,
    isMenuEditorRoute,
    intl,
    location.pathname,
    isMobileView,
  ]);

  useEffect(() => {
    if (isMobileView && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- would trigger a recursive re-render otherwise
  }, [location.pathname]);

  return (
    <Box>
      <Hidden xlUp>
        <Drawer
          anchor="left"
          onClose={onMobileClose}
          open={isMobileView}
          variant="temporary"
          sx={styles.mobileStyle}
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden xlDown>
        <Drawer anchor="left" open variant="persistent" sx={styles.desktopStyle}>
          {content}
        </Drawer>
      </Hidden>
    </Box>
  );
};
